import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, switchMap, tap } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';

/**
 * Maps stable navigation item ids (as defined in the mock navigation data)
 * to their Transloco translation key. Titles are translated here, at the
 * point the navigation is consumed, rather than in the mock API layer,
 * since translation is a presentation concern.
 */
export const NAVIGATION_TITLE_KEYS: Record<string, string> = {
  dashboard: 'navigation.dashboard',
  proposal: 'navigation.proposals',
  offers: 'navigation.offers',
  requests: 'navigation.request',
  fretes: 'navigation.shipping',
  crm: 'navigation.crm',
  'crm.clients': 'navigation.customers',
  'crm.suppliers': 'navigation.suppliers',
  'reference-data': 'navigation.referenceData',
  'reference-data.species': 'navigation.species',
  'reference-data.ports': 'navigation.cargoPorts',
  'reference-data.containers': 'navigation.shippingMethods',
  'reference-data.containerTypes': 'navigation.containerTypes',
  'reference-data.companhias': 'navigation.shippingCompanies',
  'reference-data.incoterms': 'navigation.incoterms',
  'reference-data.paymentConditions': 'navigation.paymentConditions',
  'reference-data.currencies': 'navigation.currencies',
  administration: 'navigation.administration',
  'administration.settings': 'navigation.companySettings',
  'administration.users': 'navigation.users',
  'administration.import': 'navigation.import',
  'administration.roles': 'navigation.roles',
};

/**
 * Maps the raw badge titles coming from the mock navigation data to their
 * Transloco translation key.
 */
const NAVIGATION_BADGE_KEYS: Record<string, string> = {
  new: 'navigation.badgeNew',
  updated: 'navigation.badgeUpdated',
};

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private _navigation: ReplaySubject<Navigation> =
    new ReplaySubject<Navigation>(1);
  private _rawNavigation: Navigation | null = null;

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private _translocoService: TranslocoService
  ) {
    // Re-translate and re-emit the navigation whenever the active language
    // changes, so the sidebar updates live without requiring a page reload.
    // Wait for the target language file to finish loading (it may not have
    // been fetched yet the first time the user switches to it) before
    // reading translations, otherwise we'd emit untranslated fallback text.
    this._translocoService.langChanges$
      .pipe(switchMap((activeLang) => this._translocoService.load(activeLang)))
      .subscribe(() => {
        if (this._rawNavigation) {
          this._navigation.next(
            this._translateNavigation(this._rawNavigation)
          );
        }
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/navigation').pipe(
      tap((navigation) => {
        this._rawNavigation = navigation;
        this._navigation.next(this._translateNavigation(navigation));
      })
    );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Translate the titles (and badge titles) of every navigation item, in
   * every navigation layout variant, using the current active language.
   * Returns new array/object references so consumers bound via Angular
   * inputs (eg. FuseVerticalNavigationComponent) detect the change.
   *
   * @param navigation
   * @private
   */
  private _translateNavigation(navigation: Navigation): Navigation {
    return {
      compact: this._translateNavigationItems(navigation.compact),
      default: this._translateNavigationItems(navigation.default),
      futuristic: this._translateNavigationItems(navigation.futuristic),
      horizontal: this._translateNavigationItems(navigation.horizontal),
    };
  }

  /**
   * Recursively translate the title and badge title of a list of
   * navigation items, without mutating the original data.
   *
   * @param items
   * @private
   */
  private _translateNavigationItems(
    items: FuseNavigationItem[]
  ): FuseNavigationItem[] {
    if (!items) {
      return items;
    }

    return items.map((item) => {
      const translatedItem: FuseNavigationItem = { ...item };

      // Title
      const titleKey = item.id && NAVIGATION_TITLE_KEYS[item.id];
      if (titleKey) {
        translatedItem.title = this._translocoService.translate(titleKey);
      }

      // Badge
      if (item.badge?.title) {
        const badgeKey = NAVIGATION_BADGE_KEYS[item.badge.title];
        if (badgeKey) {
          translatedItem.badge = {
            ...item.badge,
            title: this._translocoService.translate(badgeKey),
          };
        }
      }

      // Children
      if (item.children) {
        translatedItem.children = this._translateNavigationItems(
          item.children
        );
      }

      return translatedItem;
    });
  }
}
